import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface ScenarioDataPoint {
    scenario: string;
    revenue: number;
    expenses: number;
    profit: number;
}

export const WhatIfSimulatorChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo((): ScenarioDataPoint[] => {
        if (!finance?.metrics) return [];

        const { revenue = 0, expenses = 0, adSpend = 0 } = finance.metrics as Record<string, number>;

        if (revenue === 0 && expenses === 0) return [];

        // Current ROAS: how much revenue generated per dollar of ad spend
        const roas = adSpend > 0 ? revenue / adSpend : 0;
        // Non-ad expenses (fixed costs, COGS, etc.)
        const nonAdExpenses = expenses - adSpend;

        // Scenario: -20% Ad Spend
        const lowAdSpend = adSpend * 0.8;
        const lowRevenue = roas * lowAdSpend;
        const lowExpenses = nonAdExpenses + lowAdSpend;
        const lowProfit = lowRevenue - lowExpenses;

        // Scenario: +20% Ad Spend
        const highAdSpend = adSpend * 1.2;
        const highRevenue = roas * highAdSpend;
        const highExpenses = nonAdExpenses + highAdSpend;
        const highProfit = highRevenue - highExpenses;

        const currentProfit = revenue - expenses;

        return [
            {
                scenario: t('charts.scenarioLow', '-20% Ad Spend'),
                revenue: lowRevenue,
                expenses: lowExpenses,
                profit: lowProfit,
            },
            {
                scenario: t('charts.scenarioCurrent', 'Current'),
                revenue,
                expenses,
                profit: currentProfit,
            },
            {
                scenario: t('charts.scenarioHigh', '+20% Ad Spend'),
                revenue: highRevenue,
                expenses: highExpenses,
                profit: highProfit,
            },
        ];
    }, [finance?.metrics, t]);

    const currentProfit = useMemo(() => {
        const current = chartData.find(d => d.scenario === t('charts.scenarioCurrent', 'Current'));
        return current?.profit ?? 0;
    }, [chartData, t]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: currentProfit >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense }}>
                {t('charts.currentProfit', 'Current Profit')}: {formatCompactNumber(currentProfit, currencySymbol)}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="scenario"
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => {
                                const label = name === 'revenue'
                                    ? t('charts.revenue', 'Revenue')
                                    : name === 'expenses'
                                        ? t('charts.expenses', 'Expenses')
                                        : t('charts.profit', 'Profit');
                                return [formatCurrency(value, currencySymbol), label];
                            }}
                        />
                        <Legend
                            formatter={(value: string) => {
                                if (value === 'revenue') return t('charts.revenue', 'Revenue');
                                if (value === 'expenses') return t('charts.expenses', 'Expenses');
                                if (value === 'profit') return t('charts.profit', 'Profit');
                                return value;
                            }}
                            wrapperStyle={{ fontSize: 10, color: 'var(--text-primary)' }}
                        />
                        <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                        <Bar
                            dataKey="revenue"
                            name="revenue"
                            fill={CHART_COLORS.revenue}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="expenses"
                            name="expenses"
                            fill={CHART_COLORS.expense}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="profit"
                            name="profit"
                            fill={CHART_COLORS.profit}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
